import { defineStore } from "pinia";
import { useFetch } from "#app";
import {
  type IVisibilityMenu,
  type IPermissionDefineStore,
  parseJwtPayload,
  RoleType,
  type IPermission,
  type IVisibilityMenuOperator,
  type IVisibilityMenuAgent,
  MenuType,
} from "~/types/permission/Types";

export const usePermissionStore = defineStore("PermissionStore", {
  state: (): IPermissionDefineStore => ({
    error: null,
    rolePermission: null,
    rolePermissionPersist: null,
    visibilityMenu: null,
    visibilityMenuOperator: null,
    visibilityMenuAgent: null,
  }),
  getters: {},
  actions: {
    async getVisibilityMenu() {
      this.error = null;
      try {
        const { data, error } = await useFetch<IVisibilityMenu>(
          "/api/permission/visibilityMenu"
        );
        this.visibilityMenu = data.value;

        let visibilityMenu = Object.keys(this.visibilityMenu).filter(
          (k) => this.visibilityMenu[k]
        );
        let jwt = parseJwtPayload();
        let role = jwt.nickname
          ? RoleType.Agent
          : jwt.adphone
          ? RoleType.Operator
          : RoleType.Admin;
        let role2 = "";
        let permission = new Map<string, IPermission>([
          // ---------------- Menu Users ----------------
          [
            "root/users",
            {
              role: "root",
              title: "Users settings",
              adminP: [
                "callcenter",
                "ManageRoles",
                "ManageRoles",
                "messageOpt",
              ],
              operatorP: ["list_operators", "users", "role_managemnt", "message_to_operators", "whats_app"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "users/operator",
            {
              role: "users",
              title: "Definition of operator",
              adminP: ["callcenter"],
              operatorP: ["users"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "users/list",
            {
              role: "",
              title: "List of users",
              adminP: ["ManageRoles"],
              operatorP: ["users"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "users/role",
            {
              role: "",
              title: "Role management",
              adminP: ["ManageRoles"],
              operatorP: ["users"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "users/message",
            {
              role: "message_to_operators",
              title: "Messages to operators",
              adminP: ["messageOpt"],
              operatorP: ["message_to_operators"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "users/whatsapp",
            {
              role: "",
              title: "whatsapp",
              adminP: ["whatsApp"],
              operatorP: ["whats_app"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "users/whatsappMessages",
            {
              role: "",
              title: "whatsappMessages",
              adminP: ["ci"],
              operatorP: ["whats_app"],
              agentP: [],
              visible: true,
            },
          ],
          // ---------------- Menu Contacts ----------------
          [
            "root/contacts",
            {
              role: "root",
              title: "Contacts",
              adminP: ["ci"],
              operatorP: ["ci_persons", "ci_companies"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "contacts/people",
            {
              role: "ci_persons",
              title: "people",
              adminP: ["ci"],
              operatorP: ["ci_persons"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "contacts/company",
            {
              role: "ci_companies",
              title: "company",
              adminP: ["ci"],
              operatorP: ["ci_companies"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "contacts/whatsapp",
            {
              role: "",
              title: "whatsapp",
              adminP: ["whatsApp"],
              operatorP: ["ci_whatsapp"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "contacts/whatsappMessages",
            {
              role: "",
              title: "whatsapp",
              adminP: ["whatsApp"],
              operatorP: ["ci_whatsapp"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "contacts/products",
            {
              role: "",
              title: "products",
              adminP: ["ci"],
              operatorP: ["ci_products"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "contacts/invoice",
            {
              role: "factors",
              title: "invoice",
              adminP: ["ci"],
              operatorP: ["ci_persons"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "contacts/invoice/form",
            {
              role: "factors",
              title: "invoice",
              adminP: ["ci"],
              operatorP: ["ci_persons"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "contacts/invoice/setting",
            {
              role: "factors",
              title: "invoice",
              adminP: ["ci"],
              operatorP: ["ci_persons"],
              agentP: [],
              visible: true,
            },
          ],  
          [
            "contacts/inventory",
            {
              role: "",
              title: "inventory",
              adminP: ["ci"],
              operatorP: ["ci_inventory"],
              agentP: [],
              visible: true,
            },
          ],

          // ---------------- Menu Settings ----------------
          [
            "root/settings",
            {
              role: "root",
              title: "Call Setting",
              adminP: [
                "callfrwdNumDef",
                "callcenter",
                "callcenter",
                "callcenter",
                "timeCond",
                "voicemail",
                "callcenter",
                "callcenter",
                "callcenter",
              ],
              operatorP: [
                "call_forward",
                "upload_voice",
                "queue",
                "ivr_menu",
                "time_cond",
                "voicemail_management",
                "block_list",
                "destinations",
                "callcenter",
              ],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/forward",
            {
              role: "call_forward",
              title: "Call Forward",
              adminP: ["callfrwdNumDef"],
              operatorP: ["call_forward"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/recording",
            {
              role: "voices",
              title: "Upload Recording",
              adminP: ["callcenter"],
              operatorP: ["voices"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/queue",
            {
              role: "queue",
              title: "Queue",
              adminP: ["callcenter"],
              operatorP: ["queue"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/ivr",
            {
              role: "ivr_menu",
              title: "Manage IVR",
              adminP: ["callcenter"],
              operatorP: ["ivr_menu"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/timeCondition",
            {
              role: "time_cond",
              title: "Set Time Condition",
              adminP: ["timeCond"],
              operatorP: ["time_cond"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/voiceMail",
            {
              role: "voicemail",
              title: "Manage Voicemail",
              adminP: ["voiceMan"],
              operatorP: ["voicemail"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/blackList",
            {
              role: "block_list",
              title: "Black List",
              adminP: ["callcenter"],
              operatorP: ["block_list"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/destination",
            {
              role: "destinations",
              title: "Call Destination",
              adminP: ["callcenter"],
              operatorP: ["destinations"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "settings/rating",
            {
              role: "",
              title: "Ratings",
              adminP: ["callcenter"],
              operatorP: [],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu Monitoring ----------------
          [
            "root/monitoring",
            {
              role: "root",
              title: "Monitoring",
              adminP: ["true", "wallboard", "liveCalMon"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "monitoring/dashboard",
            {
              role: "",
              title: "Dashboard",
              adminP: ["true"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "monitoring/wallboard",
            {
              role: "wallboard",
              title: "Wallboard",
              adminP: ["wallboard"],
              operatorP: ["wallboard"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "monitoring/call",
            {
              role: "live_monitoring",
              title: "Live Call",
              adminP: ["liveCalMon"],
              operatorP: ["live_monitoring"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu send ------------------
          [
            "root/send",
            {
              role: "root",
              title: "send",
              adminP: ["sms"],
              operatorP: ["message_single", "message_group", "message_bulk", "message_report"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/send-single",
            {
              role: "",
              title: "send a single SMS",
              adminP: ["sms"],
              operatorP: ["message_single"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/send-single/edit",
            {
              role: "",
              title: "Sms",
              adminP: ["sms"],
              operatorP: ["message_single"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/report-send",
            {
              role: "",
              title: "report send",
              adminP: ["sms"],
              operatorP: ["message_report"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/report-receive",
            {
              role: "",
              title: "report receive",
              adminP: ["sms"],
              operatorP: ["message_report"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/bulk",
            {
              role: "",
              title: "send a bulk",
              adminP: ["sms"],
              operatorP: ["message_bulk"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/bulk/edit",
            {
              role: "",
              title: "bulk",
              adminP: ["sms"],
              operatorP: ["message_bulk"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/groups",
            {
              role: "",
              title: "send a groups SMS",
              adminP: ["sms"],
              operatorP: ["message_group"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/groups/create",
            {
              role: "",
              title: "groups",
              adminP: ["sms"],
              operatorP: ["message_group"],
              agentP: [],
              visible: true,
            },
          ],
          [
            "send/groups/edit",
            {
              role: "",
              title: "groups",
              adminP: ["sms"],
              operatorP: ["message_group"],
              agentP: [],
              visible: true,
            },
          ],
          // ---------------- Menu Report ----------------
          [
            "root/report",
            {
              role: "root",
              title: "report",
              adminP: [
                "hangupStatus",
                "answeredCalls",
                "nAnsCal",
                "optActivity",
                "recCalLg",
                "advLgs",
                "scatRep",
                "rateLogs",
                "callcenter",
                "advLgs",
              ],
              operatorP: [
                "call_report_status",
                "call_report_answerd",
                "call_report_unanswerd",
                "operators_activity",
                "call_logs",
                "full_call_records",
                "call_scattering",
                "rating",
                "cost_log",
                "customer_waiting",
              ],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/hangupReason",
            {
              role: "",
              title: "hangupReason",
              adminP: ["hangupStatus"],
              operatorP: ["call_logs"],
              agentP: [],
              visible: false,
            },
          ],
          // [
          //   "report/answered",
          //   {
          //     role: "call_report_answerd",
          //     title: "answered",
          //     adminP: ["answeredCalls"],
          //     operatorP: ["call_report_answerd"],
          //     agentP: [],
          //     visible: false,
          //   },
          // ],
          [
            "report/calls",
            {
              role: "call_report_unanswerd",
              title: "calls",
              adminP: ["nAnsCal"],
              operatorP: ["call_report_unanswerd"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/operatorActivity",
            {
              role: "operators_activity",
              title: "operatorActivity",
              adminP: ["optActivity"],
              operatorP: ["operators_activity"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/cdr",
            {
              role: "call_logs",
              title: "cdr",
              adminP: ["recCalLg"],
              operatorP: ["call_logs"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/fullCdrs",
            {
              role: "full_call_logs",
              title: "fullCdrs",
              adminP: ["advLgs"],
              operatorP: ["full_call_logs"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/scatteirng",
            {
              role: "call_scattering",
              title: "scatteirng",
              adminP: ["scatRep"],
              operatorP: ["call_scattering"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/rating",
            {
              role: "rating",
              title: "rating",
              adminP: ["rateLogs"],
              operatorP: ["rating"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/cost",
            {
              role: "cost_log",
              title: "cost",
              adminP: ["callcenter"],
              operatorP: ["cost_log"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "report/waiting",
            {
              role: "customer_waiting",
              title: "waiting",
              adminP: ["advLgs"],
              operatorP: ["customer_waiting"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu PerformanceEvaluation ----------------
          [
            "root/evaluation",
            {
              role: "root",
              title: "Performance Evaluation",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/save",
            {
              role: "",
              title: "Save performance Evaluation",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/save/form",
            {
              role: "",
              title: "Save performance Evaluation",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/save/edit",
            {
              role: "",
              title: "Save performance Evaluation",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/save/view",
            {
              role: "",
              title: "Save performance Evaluation",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/report",
            {
              role: "",
              title: "Performance Evaluation Report",
              adminP: ["qaqc"],
              operatorP: ["evaluation_report"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "evaluation/user",
            {
              role: "",
              title: "Performance Evaluation Report",
              adminP: ["qaqc"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],

          // ---------------- Menu Support ----------------
          [
            "root/support",
            {
              role: "root",
              title: "Support",
              adminP: ["support"],
              operatorP: ["create_tickets", "sent_tickets"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "support/send",
            {
              role: "create_tickets",
              title: "Insert New Ticket",
              adminP: ["support"],
              operatorP: ["create_tickets"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "support/tickets",
            {
              role: "sent_tickets",
              title: "Tickets List",
              adminP: ["support"],
              operatorP: ["sent_tickets"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu CRM ----------------
          [
            "root/crm",
            {
              role: "root",
              title: "Connect to CRM",
              adminP: ["crm"],
              operatorP: [
                "crm_authorized_numbers",
                "crm_settings",
                "crm_links",
              ],
              agentP: [],
              visible: false,
            },
          ],
          [
            "crm/numbers",
            {
              role: "crm_authorized_numbers",
              title: "CRM Numbers",
              adminP: ["crm"],
              operatorP: ["crm_authorized_numbers"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "crm/settings",
            {
              role: "crm_settings",
              title: "CRM Structure",
              adminP: ["crm"],
              operatorP: ["crm_settings"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "crm/links",
            {
              role: "crm_links",
              title: "CRM links",
              adminP: ["crm"],
              operatorP: ["crm_links"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu Fax ----------------
          [
            "root/fax",
            {
              role: "root",
              title: "Fax",
              adminP: ["fax"],
              operatorP: [
                "fax_create",
                "fax_send",
                "fax_sent_report",
                "fax_recv_report",
              ],
              agentP: [],
              visible: false,
            },
          ],
          [
            "fax/insert",
            {
              role: "fax_create",
              title: "Insert New Fax",
              adminP: ["fax"],
              operatorP: ["fax_create"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "fax/send",
            {
              role: "fax_send",
              title: "Send Fax",
              adminP: ["fax"],
              operatorP: ["fax_send"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "fax/submitted",
            {
              role: "fax_sent_report",
              title: "Report of Submitted Fax",
              adminP: ["fax"],
              operatorP: ["fax_sent_report"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "fax/received",
            {
              role: "fax_recv_report",
              title: "Report of Received Fax",
              adminP: ["fax"],
              operatorP: ["fax_recv_report"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Menu Webchat ----------------
          [
            "root/Webchat",
            {
              role: "root",
              title: "Webchat",
              adminP: ["webchat"],
              operatorP: [
                "webchat_declare_website",
                "webchat_operator",
                "webchat_roles_operator_management",
                "webchat_roles_operator_management",
                "webchat_deparment",
                "webchat_personalization",
                "webchat_chats",
                "webchat_counter",
              ],
              agentP: [
                "center_settings",
                "numbers",
                "user_settings",
                "access",
                "center_settings",
                "conversations",
                "conversations",
              ],
              visible: false,
            },
          ],
          [
            "Webchat/settings",
            {
              role: "webchat_declare_website",
              title: "Insert New Websit",
              adminP: ["webchat"],
              operatorP: ["webchat_declare_website"],
              agentP: ["center_settings"],
              visible: false,
            },
          ],
          [
            "Webchat/number",
            {
              role: "webchat_operator",
              title: "Insert Operator Number",
              adminP: ["webchat"],
              operatorP: ["webchat_operator"],
              agentP: ["numbers"],
              visible: false,
            },
          ],
          [
            "Webchat/user",
            {
              role: "webchat_roles_operator_management",
              title: "Manage Operator",
              adminP: ["webchat"],
              operatorP: ["webchat_roles_operator_management"],
              agentP: ["user_settings"],
              visible: false,
            },
          ],
          [
            "Webchat/role",
            {
              role: "",
              title: "Manage Roles",
              adminP: ["webchat"],
              operatorP: ["webchat_roles_operator_management"],
              agentP: ["access"],
              visible: false,
            },
          ],
          [
            "Webchat/department",
            {
              role: "webchat_deparment",
              title: "Department",
              adminP: ["webchat"],
              operatorP: ["webchat_deparment"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "Webchat/customize",
            {
              role: "webchat_personalization",
              title: "Customize",
              adminP: ["webchat"],
              operatorP: ["webchat_personalization"],
              agentP: ["center_settings"],
              visible: false,
            },
          ],
          [
            "Webchat/conversations",
            {
              role: "webchat_chats",
              title: "Conversations",
              adminP: ["webchat"],
              operatorP: ["webchat_chats"],
              agentP: ["conversations"],
              visible: false,
            },
          ],
          [
            "Webchat/dashboard",
            {
              role: "webchat_counter",
              title: "Dashboard",
              adminP: ["webchat"],
              operatorP: ["webchat_counter"],
              agentP: ["conversations"],
              visible: false,
            },
          ],
          // ---------------- Menu VideoConference ----------------
          [
            "root/videoConference",
            {
              role: "root",
              title: "videoConference",
              adminP: ["videoconf"],
              operatorP: ["conf_create_link", "conf_report"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "videoConference/createLink",
            {
              role: "",
              title: "createLink",
              adminP: ["videoconf"],
              operatorP: ["conf_create_link"],
              agentP: [],
              visible: false,
            },
          ],
          [
            "videoConference/report",
            {
              role: "",
              title: "report",
              adminP: ["videoconf"],
              operatorP: ["conf_report"],
              agentP: [],
              visible: false,
            },
          ],
          // ---------------- Notification ----------------
          [
            "notification",
            {
              role: "",
              title: "Notification",
              adminP: ["true"],
              operatorP: ["true"],
              agentP: [],
              visible: false,
            },
          ],
        ]);

        visibilityMenu.push("true");
        for (let [key, value] of permission) {
          value.visible = value.adminP.some((item) =>
            visibilityMenu.includes(item)
          );
          permission.set(key, value);
        }

        if (role == RoleType.Operator) {
          await this.getVisibilityMenuOperator();
          if (!this.visibilityMenuOperator?.permissions) {
            this.visibilityMenuOperator.permissions = [];
          }
          this.visibilityMenuOperator?.permissions.push("true");
          role2 = this.visibilityMenuOperator?.role;
          for (let [key, value] of permission) {
            value.visible =
              value.visible &&
              value.operatorP.some((item) =>
                this.visibilityMenuOperator?.permissions.includes(item)
              );
            permission.set(key, value);
          }
        } else if (role == RoleType.Agent) {
          // await this.getVisibilityMenuAgent();
          for (let [key, value] of permission) {
            value.visible = false;
            permission.set(key, value);
          }
        }

        this.rolePermission = {
          role: role,
          role2: role2,
          permission: permission,
        };
        this.rolePermissionPersist = [...permission];

        this.error = error;
      } catch (err: any) {
        this.error = err;
      }
    },
    async getVisibilityMenuOperator() {
      this.error = null;
      try {
        const { data, error } = await useFetch<IVisibilityMenuOperator>(
          "/api/permission/visibilityMenuOperator"
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.visibilityMenuOperator = data.value;
      } catch (err: any) {
        this.error = err;
      }
    },
    async getVisibilityMenuAgent() {
      this.error = null;
      try {
        const { data, error } = await useFetch<IVisibilityMenuAgent>(
          "/api/permission/visibilityMenuAgent"
        );
        this.error = error;
        if (error && error.value?.statusCode) {
          throw error;
        }

        this.visibilityMenuAgent = data.value;
      } catch (err: any) {
        this.error = err;
      }
    },
  },

  persist: {
    storage: persistedState.localStorage,
  },
});
